import css from './ItemsTypesTableRow.module.css';
import React, { useState } from 'react';
import { ItemTypeEntity } from '@entities/ItemType/Types';
import { HasItems } from '@modules/Steps/Shared/Helpers/HasItems';
import { ItemTableRow } from '@modules/Steps/Components/Step2/Step2DesktopContent/FragmentInfoStep2/ItemsTypesTable/ItemTableRow/ItemTableRow';
import ExclamationIcon from '@shared/Icons/ExclamationIcon';
import { ItemSelectionModal } from '@modules/Steps/Shared/Widgets/Modals/ItemsSelectionModal/ItemsSelectionModal';
import { useParams } from 'react-router-dom';
import BasicButton from '@shared/Ui/Buttons/BasicButton/BasicButton';
import { StepsViewModeEnum } from '@shared/Enums/StepsViewModeEnum';
import PlusIcon from '@shared/Icons/PlusIcon';
import { useFragmentsStore } from '../../../../../../../../Stores/FragmentsStore';
import { FormatNumber } from '@shared/Helpers/FormatValue/FormatNumber/FormatNumber';

interface ItemsTypesTableRowProps {
    itemType: ItemTypeEntity;
}

export const ItemsTypesTableRow: React.FC<ItemsTypesTableRowProps> = ({
    itemType,
}) => {
    const { itemTypesWithItems } = useFragmentsStore();
    const { viewMode } = useParams();

    const { title, qty, unit, fairMarketPrice } = itemType;

    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const isItemTypeWithItems = HasItems(itemType.id, itemTypesWithItems);

    const renderTableRow = () => {
        if (itemType.items?.length) {
            return (
                <div className={css.itemType}>
                    {itemType.items.map(item => (
                        <ItemTableRow item={item} key={item.uuid} />
                    ))}
                    <BasicButton
                        className={css.currentItem}
                        disabled={itemType.labor}
                        onClick={handleOpenModal}
                    >
                        Manage products
                    </BasicButton>
                </div>
            );
        }

        if (fairMarketPrice && !isItemTypeWithItems) {
            const totalPrice = parseFloat(fairMarketPrice) * qty;

            return (
                <div className={css.emptyItemTableRow}>
                    <div className={css.emptyItemType}>Fair market price</div>
                    <div className={css.qty} />
                    <div className={css.price}>R {fairMarketPrice}</div>
                    <div className={css.total}>
                        R {FormatNumber(totalPrice, '.', 2)}
                    </div>
                </div>
            );
        }

        if (isItemTypeWithItems) {
            return (
                <div className={css.emptyItemTableRow}>
                    <div className={css.itemCell}>
                        <BasicButton
                            className={css.currentItem}
                            disabled={itemType.labor}
                            onClick={handleOpenModal}
                        >
                            <PlusIcon
                                width={25}
                                height={25}
                                stroke={
                                    viewMode ===
                                        StepsViewModeEnum.PRESENTATION ||
                                    itemType.labor
                                        ? '#9D9D9D'
                                        : '#0E7B51'
                                }
                            />
                            Select product
                        </BasicButton>
                    </div>

                    <div className={css.qty} />
                    <div className={css.price}>R 0.00</div>
                    <div className={css.total}>R 0.00</div>
                </div>
            );
        }

        return (
            <div className={css.emptyItemTableRow}>
                <div className={css.emptyItemType}>
                    <div className={css.emptyItemTypeIcon}>
                        <ExclamationIcon />
                    </div>
                    <div>There is no option to select a product!</div>
                </div>
                <div className={css.qty} />
                <div className={css.price}>R 0.00</div>
                <div className={css.total}>R 0.00</div>
            </div>
        );
    };

    return (
        <div className={css.itemTableRow}>
            <div className={css.tableRow}>
                <div className={css.itemTypeCell}>
                    <span>{title}</span>
                    <div className={css.quantityUnit}>
                        <span>{qty}</span>
                        <span>{unit}</span>
                    </div>
                </div>
            </div>
            {renderTableRow()}

            {isModalOpen && (
                <ItemSelectionModal
                    onClose={handleCloseModal}
                    itemType={itemType}
                />
            )}
        </div>
    );
};
